import { supabase } from '../lib/supabase';

/** Dedicated bucket for GOMTB Register — namespaced for shared Supabase projects */
export const REGISTERAPP_EVENT_IMAGES_BUCKET = 'registerapp-event-images';

const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];
const EXTENSION_TO_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};
const ALLOWED_MIME_TYPES = Object.values(EXTENSION_TO_MIME);

function getFileExtension(fileName: string): string {
  const parts = fileName.toLowerCase().split('.');
  return parts.length > 1 ? parts[parts.length - 1] : '';
}

function resolveContentType(file: File): string | null {
  const extension = getFileExtension(file.name);
  const mimeFromExtension = EXTENSION_TO_MIME[extension];

  if (file.type && ALLOWED_MIME_TYPES.includes(file.type)) {
    return file.type;
  }

  return mimeFromExtension || null;
}

function getStorageErrorMessage(error: unknown): string {
  const message =
    error && typeof error === 'object' && 'message' in error
      ? String((error as { message: string }).message)
      : '';

  if (!message) {
    return 'Failed to upload image. Please try again.';
  }

  const lowerMessage = message.toLowerCase();

  if (lowerMessage.includes('bucket not found')) {
    return 'Image storage is not configured yet. Please run setup-registerapp-event-images-storage.sql in Supabase.';
  }

  if (
    lowerMessage.includes('row-level security') ||
    lowerMessage.includes('permission') ||
    lowerMessage.includes('not authorized')
  ) {
    return 'You do not have permission to upload images. Please sign out, sign in again, and retry.';
  }

  if (lowerMessage.includes('payload too large') || lowerMessage.includes('file size')) {
    return 'Image size must be less than 5MB.';
  }

  if (lowerMessage.includes('mime') || lowerMessage.includes('content type')) {
    return 'Only JPEG, PNG, and WebP images are allowed.';
  }

  return `Image upload failed: ${message}`;
}

export const storageService = {
  async uploadEventImage(file: File): Promise<string> {
    const validation = this.validateImageFile(file);
    if (!validation.isValid) {
      throw new Error(validation.error || 'Invalid image file');
    }

    const contentType = resolveContentType(file);
    if (!contentType) {
      throw new Error('Only JPEG, PNG, and WebP images are allowed');
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Your session has expired. Please sign in again before uploading images.');
    }

    const extension = getFileExtension(file.name) || contentType.split('/')[1];
    const timestamp = Date.now();
    const fileName = `${timestamp}_${Math.random().toString(36).substring(2)}.${extension}`;
    const filePath = `events/${fileName}`;

    const { error } = await supabase.storage
      .from(REGISTERAPP_EVENT_IMAGES_BUCKET)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
        contentType,
      });

    if (error) {
      console.error('Error uploading image:', error);
      throw new Error(getStorageErrorMessage(error));
    }

    const { data: urlData } = supabase.storage
      .from(REGISTERAPP_EVENT_IMAGES_BUCKET)
      .getPublicUrl(filePath);

    return urlData.publicUrl;
  },

  async deleteEventImage(imageUrl: string): Promise<void> {
    try {
      const urlParts = imageUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];
      const filePath = `events/${fileName}`;

      const { error } = await supabase.storage
        .from(REGISTERAPP_EVENT_IMAGES_BUCKET)
        .remove([filePath]);

      if (error) {
        console.error('Error deleting image:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in deleteEventImage:', error);
    }
  },

  validateImageFile(file: File): { isValid: boolean; error?: string } {
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      return { isValid: false, error: 'Image size must be less than 5MB' };
    }

    const extension = getFileExtension(file.name);
    const contentType = resolveContentType(file);

    if (!contentType || !ALLOWED_EXTENSIONS.includes(extension)) {
      return { isValid: false, error: 'Only JPEG, PNG, and WebP images are allowed' };
    }

    return { isValid: true };
  },
};
